/**
 * outputHandlesFor — pure mapper from (kind, config) to canvas handles.
 *
 * MIRRORS ``hub.workflow.catalog.output_handles_for`` on the backend.
 * If you add a new multi-output node kind there, mirror the case here.
 * Single-output nodes return [] which makes TrayNodeBox render an
 * unnamed handle (the historical look).
 *
 * Lives in its own file (not WorkflowEditor.tsx) so unit tests can
 * import it without pulling in ReactFlow, the EventStream provider,
 * Monaco, and the rest of the editor surface.
 */
import type { NodeKind } from "@/lib/api-workflows";


export interface OutputHandleSpec {
  id: string;
  label: string;
}


export function outputHandlesFor(
  kind: NodeKind,
  config: Record<string, unknown>,
): OutputHandleSpec[] {
  if (kind === "if") {
    return [
      { id: "true", label: "True" },
      { id: "false", label: "False" },
    ];
  }
  // ADR-0066 S2 — an approval wait routes like IF (approved/rejected); a timer
  // wait is a plain passthrough (unnamed handle → []).
  if (kind === "wait") {
    if ((config.mode as string) === "approval") {
      return [
        { id: "approved", label: "Approved" },
        { id: "rejected", label: "Rejected" },
      ];
    }
    return [];
  }
  // The graphical batching loop exposes two handles (mirrors
  // hub.workflow.catalog.output_handles_for): `done` (top) fires once with all
  // collected items; `loop` (bottom) drives the body, whose last node edges
  // BACK here — the intended cycle. Order = done-then-loop (n8n convention:
  // done continues the main flow up top, loop hangs the body below). Without
  // this case the loop rendered a single unnamed handle, so both edges left
  // the same dot (the tangled canvas).
  if (kind === "loop") {
    return [
      { id: "done", label: "Done" },
      { id: "loop", label: "Loop" },
    ];
  }
  if (kind === "switch") {
    const cases = (config.cases as Array<Record<string, unknown>>) ?? [];
    const out: OutputHandleSpec[] = [];
    const seen = new Set<string>();
    for (const c of cases) {
      const hid = c?.handle_id as string | undefined;
      const label = (c?.label as string | undefined) ?? hid;
      if (!hid || seen.has(hid)) continue;
      seen.add(hid);
      out.push({ id: hid, label: label ?? hid });
    }
    const defaultId = (config.default_handle_id as string) || "default";
    if (!seen.has(defaultId)) {
      out.push({ id: defaultId, label: "Default" });
    }
    return out;
  }
  // ADR-0008 PR3 — sinks expose one handle per outcome branch.
  // Layout matches backend output_handles_for: implicit succeeded +
  // dead_letter FIRST (stable ordering so adding custom branches
  // doesn't shift existing canvas wires), then user-declared branches
  // in their saved order. Reserved/duplicate ids in malformed configs
  // are silently skipped so the function stays total.
  if (kind === "http_sink" || kind === "connector_sink") {
    const handles: OutputHandleSpec[] = [
      { id: "succeeded", label: "Succeeded" },
      { id: "dead_letter", label: "Dead letter" },
    ];
    const seen = new Set<string>(["succeeded", "dead_letter"]);
    const branches =
      (config.branches as Array<Record<string, unknown>>) ?? [];
    for (const b of branches) {
      const bid = b?.id as string | undefined;
      const label = (b?.label as string | undefined) ?? bid;
      if (!bid || seen.has(bid)) continue;
      seen.add(bid);
      handles.push({ id: bid, label: label ?? bid });
    }
    return handles;
  }
  return [];
}
